import React, { useEffect, useState, useMemo } from 'react';
import NewReleaseGames from '@/pages/intelligence/pages/Potential/NewReleaseGames/components/NewReleaseGames';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { history, useIntl } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import {
  commonMobileDefaultFilterValue,
  handlePotentialNewReleaseFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import {
  SENSOR_TOWER_TAG_KEYS,
  APPMAGIC_TAG_DEFAULT_VALUES,
  buildAppMagicTagSpecialParams,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import useMobileGameEngineOptions from '@intelligence/hooks/useMobileGameEngineOptions';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { getNewReleaseCompanyList } from '@/pages/intelligence/services/filter';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import { getTopNewReleaseCustomValidUrlParams, topchartNewReleaseMobileValidCfg, hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import { getMobileTopChartsFilterTips } from '@intelligence/pages/TopCharts/MobileGames/_config/mobileTopChartsFilterTips';

export default function NewRelease(props: any) {
  const { formatMessage, locale } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const {
    entityType = 'mobile',
    pageLogInfo = dbiPageLogMes.potentialNewReleaseGamesMobileGame,
    // 用于 Potential 模块独立页等不需要分组的复用场景；Top Charts Mobile NewRelease 入口不传此 prop，保留分组
    disableFilterGrouping = false,
    // 当为 true 时启用筛选器吸顶；默认 true
    enableStickyFilter = true,
    // Top Charts 手游榜单入口启用筛选器 hover 文案
    enableMobileTopChartsFilterHoverTips = false,
    pageId,
  } = props;

  const hasUrlShortLink = hasShortLinkRatio.topMobileUpcoming;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: 'bar',
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.mobliePheat,
  });
  const { tagLoading, tagConfig } = useSenTowerTag({});
  const { loadingStd: definitionsLoading, filterTags } = useQueryDefinition();
  const { mobileGameEngineOptions } = useMobileGameEngineOptions();

  const appMagicTagConfig = useMemo(
    () => buildAppMagicTagSpecialParams(filterTags, isCn),
    [isCn, filterTags],
  );

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopNewReleaseCustomValidUrlParams(history, topchartNewReleaseMobileValidCfg);
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const defaultFilterValue = {
    ...commonMobileDefaultFilterValue,
    ...APPMAGIC_TAG_DEFAULT_VALUES,
  };
  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.ALL_Games,
          // filterKeyMap.REGION,
          // filterKeyMap.PLAN,
          filterKeyMap.ALL_COMPANYS,
          // filterKeyMap.PHEAD_RANG,
        ],
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: SENSOR_TOWER_TAG_KEYS,
      },
      {
        key: 'app_magic',
        titleKey: 'dbi_filter_group_app_magic',
        iconCode: 'appmagic',
        filterKeys: filterKeyMap.APPMAGIC_TAG_KEYS,
      },
      {
        key: 'igdb',
        titleKey: 'dbi_filter_group_igdb',
        iconCode: 'igdb',
        filterKeys: [filterKeyMap.GAME_ENGINE],
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );
  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: groupedFilter.filterKeyList,
    filterGroups: disableFilterGrouping ? undefined : groupedFilter.filterGroups,
    companyFilterParams: {
      entity_type: entityType,
      search_name: '',
      page: 1,
      file_page_size: 500,
      source_metric: {},
    },
    getCompaniesFetch: getNewReleaseCompanyList,
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
    },
    specialParams: {
      ...(enableMobileTopChartsFilterHoverTips
        ? { filterTips: getMobileTopChartsFilterTips(formatMessage) }
        : {}),
      regionOptions, // 国家筛选器options,
      defalutMarket: [], // 没取到交集用的默认值
      disableNoChina: true,
      rangeTimeType: FilterTimeType.commonRange,
      disableIegg: false,
      disableAnnie: false,
      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      gameEngineOptions: mobileGameEngineOptions,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'new_release' },
      }
    },
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handlePotentialNewReleaseFilterParamsToApi(params, entityType),
    hasUrlShortLink,
    enableSticky: enableStickyFilter,
  };

  return (
    <NewReleaseGames
      entityType={entityType}
      downloadFileName={'Potential_New Release Games_Mobile Games'}
      pageLogInfo={pageLogInfo}
      filterCfg={filterCfg}
      pageId={pageId}
      stickyHeaderTitle={enableStickyFilter ? formatMessage({ id: 'dbi_mobile_games' }) : undefined}
      filterLoading={filterLoading || tagLoading || shortLinkLoading || definitionsLoading}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
  );
}
